import { useAction, useAtom } from '@reatom/npm-react'
import { useMemo, useRef } from 'react'
import styled from 'styled-components'

import { Button } from '@blade/ui-kit'
import { useTranslation } from '@root/shared/localization'
import { PremiumDialog } from '@root/shared/ui/premium-dialog'
import { UpdateLogoProgress } from '@root/widgets/premium/update-logo-drawer/ui/common/update-logo-progress'

import { reatomUpdateBannerDrawer } from '../../atoms'
import { PremiumBannerDrawerPreview } from '../common/update-banner-preview'

import type { PairModel } from '@root/entities/pair'
import type { PremiumMetaModel } from '@root/entities/premium'
import type { DialogControlProps } from '@root/shared/ui-kit'

type Props = {
  premiumMeta: PremiumMetaModel
  pair: PairModel
} & DialogControlProps

export const UploadPremiumBannerDialog: React.FC<Props> = ({
  premiumMeta,
  pair,
  open,
  onOpenChange,
}) => {
  const bannerInputRef = useRef<HTMLInputElement>(null)
  const { t } = useTranslation()

  const model = useMemo(
    () =>
      // eslint-disable-next-line @reatom/unit-naming-rule
      reatomUpdateBannerDrawer({
        premiumMeta,
      }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [],
  )
  const [requestState] = useAtom(model.updateRequestState)
  const [bannerPreview] = useAtom(model.bannerPreviewAtom)
  const [banner] = useAtom(model.bannerAtom)
  const [currentBanner] = useAtom(premiumMeta.banner)

  const uploadBanner = useAction(model.updateBannerAction)
  const setBanner = useAction(model.setBannerAction)
  const clearBanner = useAction(model.clearBannerAction)
  const reset = useAction(model.resetAction)

  const handleUploadBanner = () =>
    bannerInputRef.current?.dispatchEvent(
      new MouseEvent('click', { bubbles: true }),
    )

  const clearFileInput = () => {
    if (bannerInputRef.current) {
      bannerInputRef.current.value = ''
    }
  }
  const handleReset = () => {
    reset()
    clearFileInput()
  }
  const handleClose = () => {
    handleReset()
    onOpenChange?.(false)
  }
  const handleOpenChange = (nextOpen: boolean) => {
    if (!nextOpen) {
      handleReset()
    }
    onOpenChange?.(nextOpen)
  }
  const handleRevertBanner = () => {
    clearBanner()
    clearFileInput()
  }

  return (
    <Root
      open={open}
      onOpenChange={handleOpenChange}
      title={t('premium.update-logo.title')}
      describedBy={t('premium.update-logo.title')}
      onClose={handleClose}
    >
      <Body>
        <UpdateLogoProgress
          requestState={requestState}
          onClose={handleClose}
          onReset={handleReset}
        />
        <PremiumBannerDrawerPreview
          pair={pair}
          onUploadClick={handleUploadBanner}
          onRevertClick={bannerPreview ? handleRevertBanner : undefined}
          imagePreview={bannerPreview || currentBanner}
        />
        <Input
          ref={bannerInputRef}
          type="file"
          accept="image/*"
          onChange={(e) => {
            const file = e.target.files?.[0]
            if (file) {
              setBanner(file)
            }
          }}
        />
        <Action>
          <ButtonBack
            variant="secondary"
            radius="rounded"
            size="m"
            onClick={handleClose}
          >
            {t('common.back')}
          </ButtonBack>
          <Button
            variant="solid"
            color="primary"
            size="m"
            radius="rounded"
            disabled={!banner}
            onClick={uploadBanner}
          >
            {t('common.process')}
          </Button>
        </Action>
      </Body>
    </Root>
  )
}

const Root = styled(PremiumDialog)`
  display: grid;
  text-align: center;
`
const Body = styled.div`
  padding-top: 16px;
`

const Action = styled.footer`
  display: grid;
  grid-template-columns: 1fr 1fr;

  gap: 10px;
  padding: 42px 12px 34px;
`

const ButtonBack = styled(Button)`
  color: var(--text-11);
`

const Input = styled.input`
  display: none;
`
